import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Bell, Menu, Search, Settings, User, LogOut, Briefcase } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useCurrentTemplate } from '@/hooks/useCurrentTemplate'
import { Button } from '@/components/ui/Button'
import { Avatar } from '@/components/ui/Avatar'
import { Dropdown } from '@/components/ui/Dropdown'
import { Badge } from '@/components/ui/Badge'

interface HeaderProps {
  onMenuToggle: () => void
  isMobileMenuOpen: boolean
}

export const Header: React.FC<HeaderProps> = ({ onMenuToggle, isMobileMenuOpen }) => {
  const { user, logout } = useAuth()
  const { template, loading: templateLoading } = useCurrentTemplate()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const userMenuItems = [
    {
      label: 'Profile',
      icon: User,
      onClick: () => navigate('/profile')
    },
    {
      label: 'Settings',
      icon: Settings,
      onClick: () => navigate('/settings')
    },
    {
      label: '',
      type: 'divider' as const
    },
    {
      label: 'Logout',
      icon: LogOut,
      onClick: handleLogout,
      variant: 'danger' as const
    }
  ]

  return (
    <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Left section */}
        <div className="flex items-center space-x-4">
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onMenuToggle}
            className="lg:hidden"
            aria-label="Toggle menu"
          >
            <Menu className="h-5 w-5" />
          </Button>

          {/* Logo */}
          <Link to="/dashboard" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-jewelry-primary to-jewelry-secondary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">
                {template?.customBusinessName
                  ? template.customBusinessName.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase()
                  : template?.name
                    ? template.name.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase()
                    : 'BM'
                }
              </span>
            </div>
          </Link>

          {/* Current Template Indicator */}
          {template && !templateLoading && (
            <div className="hidden lg:flex items-center space-x-2 ml-6 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <Briefcase className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <div className="flex flex-col">
                <span className="text-xs font-medium text-gray-900 dark:text-white">
                  {template.customBusinessName || template.name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {template.businessType}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Center section - Search */}
        <div className="hidden md:flex flex-1 max-w-md mx-8">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search materials, projects..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
            />
          </div>
        </div>

        {/* Right section */}
        <div className="flex items-center space-x-3">
          {/* Mobile search button */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden"
            aria-label="Search"
          >
            <Search className="h-5 w-5" />
          </Button>

          {/* Notifications */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5" />
              <Badge
                variant="danger"
                className="absolute -top-1 -right-1 h-5 w-5 text-xs"
              >
                3
              </Badge>
            </Button>
          </div>

          {/* User menu */}
          <Dropdown
            trigger={
              <Button variant="ghost" size="sm" className="flex items-center space-x-2">
                <Avatar
                  src={user?.avatar}
                  alt={user?.name || 'User'}
                  size="sm"
                  fallback={user?.name?.charAt(0) || 'U'}
                />
                <span className="hidden sm:block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {user?.name || 'User'}
                </span>
              </Button>
            }
            items={userMenuItems}
            align="right"
          />
        </div>
      </div>

      {/* Mobile search bar */}
      <div className="md:hidden px-4 pb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search materials, projects..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
          />
        </div>
      </div>
    </header>
  )
}